import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { InstagramLogo, MagnifyingGlass, UsersThree } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

import { pulseContent } from "../data/pulseContent";
import { FooterSection, MobileBottomBar, SiteHeader } from "./PulseFitnessPage";

const preferredCoachOrder = [
  "Umair Ansari",
  "Ahmed Hassan",
  "Adnan Amin",
  "Omair Malik",
  "Muhammad Sadi",
  "Tabinda Khan",
  "Zainab Mansoor",
  "Amal Balushi",
  "Sohail Saeed",
  "Dr. Momin Panhwar (DPT, CMT)",
];

const UMAIR_ROUTE = "/coaches/umair-ansari";

const revealVariant = {
  hidden: { opacity: 0, y: 28, filter: "blur(10px)" },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, delay, ease: [0.2, 0.65, 0.2, 1] },
  }),
};

const RevealCard = ({ children, className = "", delay = 0 }) => (
  <motion.div
    className={className}
    custom={delay}
    initial="hidden"
    viewport={{ once: true, amount: 0.15 }}
    whileInView="visible"
    variants={revealVariant}
  >
    {children}
  </motion.div>
);

export const TrainerDirectoryPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const trainerCount = pulseContent.trainerCategories.reduce((sum, category) => sum + category.trainers.length, 0);
  const allTrainers = useMemo(
    () =>
      pulseContent.trainerCategories.flatMap((category) =>
        category.trainers.map((trainer) => ({
          ...trainer,
          category: category.title,
          isRealProfile: Boolean(trainer.isRealProfile),
        })),
      ),
    [],
  );

  const filteredTrainers = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    const preferredOrderMap = new Map(
      preferredCoachOrder.map((name, index) => [name, index]),
    );

    return allTrainers
      .filter((trainer) => {
      const matchesCategory = selectedCategory === "All" || trainer.category === selectedCategory;
      const matchesSearch =
        !search ||
        trainer.name.toLowerCase().includes(search) ||
        trainer.specialty.toLowerCase().includes(search) ||
        trainer.role.toLowerCase().includes(search);

      return matchesCategory && matchesSearch;
      })
      .sort((a, b) => {
        const aPreferredIndex = preferredOrderMap.has(a.name)
          ? preferredOrderMap.get(a.name)
          : Number.MAX_SAFE_INTEGER;
        const bPreferredIndex = preferredOrderMap.has(b.name)
          ? preferredOrderMap.get(b.name)
          : Number.MAX_SAFE_INTEGER;

        if (aPreferredIndex !== bPreferredIndex) {
          return aPreferredIndex - bPreferredIndex;
        }

        if (a.isRealProfile !== b.isRealProfile) {
          return Number(b.isRealProfile === true) - Number(a.isRealProfile === true);
        }

        return a.name.localeCompare(b.name);
      });
  }, [allTrainers, searchTerm, selectedCategory]);

  return (
    <main className="app-shell" data-testid="trainer-directory-page">
      <SiteHeader navItems={pulseContent.navItems} />

      <section className="section-shell pb-14 pt-32" data-testid="trainer-directory-hero">
        <div className="shell grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-end">
          <RevealCard className="space-y-6" delay={0.05}>
            <p className="eyebrow" data-testid="trainer-directory-eyebrow">
              Coaching directory
            </p>
            <h1 className="hero-title max-w-4xl" data-testid="trainer-directory-title">
              Meet the Coaches
            </h1>
            <p className="hero-copy" data-testid="trainer-directory-copy">
              Train with certified professionals dedicated to performance, technique, and results.
            </p>
          </RevealCard>

          <RevealCard className="floating-panel grid gap-5 p-6 md:grid-cols-3 md:p-8" delay={0.12}>
            <div data-testid="trainer-directory-stat-total" className="space-y-2">
              <UsersThree className="h-8 w-8 text-[#2D5A5A]" />
              <p className="text-3xl font-semibold tracking-tight text-[#1A2525]">
                {trainerCount}
              </p>
              <p className="eyebrow">Total trainers</p>
            </div>
            <div data-testid="trainer-directory-stat-categories" className="space-y-2">
              <p className="text-3xl font-semibold tracking-tight text-[#1A2525]">
                {pulseContent.trainerCategories.length}
              </p>
              <p className="eyebrow">Categories</p>
            </div>
            <div data-testid="trainer-directory-stat-instagram" className="space-y-2">
              <p className="text-3xl font-semibold tracking-tight text-[#1A2525]">
                Insta
              </p>
              <p className="eyebrow">Direct coach link</p>
            </div>
          </RevealCard>
        </div>
      </section>

      <section className="section-shell pt-0" data-testid="trainer-directory-filters">
        <div className="shell space-y-6">
          <RevealCard className="floating-panel space-y-5 p-6 md:p-8" delay={0.15}>
            <div className="relative">
              <MagnifyingGlass className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#758A8A]" />
              <input
                className="pulse-input pl-12"
                data-testid="coach-search-input"
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by coach name or specialization"
                value={searchTerm}
              />
            </div>

            <div className="flex flex-wrap gap-3" data-testid="coach-filter-group">
              <button
                className={`pulse-button-outline rounded-full px-5 py-3 text-sm ${selectedCategory === "All" ? "bg-[#2D5A5A] text-white" : ""}`}
                data-testid="coach-filter-all"
                onClick={() => setSelectedCategory("All")}
                type="button"
              >
                All Coaches
              </button>

              {pulseContent.trainerCategories.map((category) => (
                <button
                  className={`pulse-button-outline rounded-full px-5 py-3 text-sm ${selectedCategory === category.title ? "bg-[#2D5A5A] text-white" : ""}`}
                  data-testid={`coach-filter-${category.slug}`}
                  key={category.slug}
                  onClick={() => setSelectedCategory(category.title)}
                  type="button"
                >
                  {category.title}
                </button>
              ))}
            </div>
          </RevealCard>

          <div className="flex items-center justify-between gap-4">
            <p className="eyebrow" data-testid="coach-results-count">
              Showing {filteredTrainers.length} of {trainerCount} coaches
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
            {filteredTrainers.length > 0 ? (
              filteredTrainers.map((trainer, trainerIndex) => (
                <RevealCard
                  className="floating-panel overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(45,90,90,0.12)]"
                  delay={trainerIndex * 0.02}
                  key={trainer.slug}
                >
                  {trainer.name === "Umair Ansari" && (
                    <Link
                      className="block"
                      data-testid="umair-ansari-card-link"
                      to={UMAIR_ROUTE}
                    >
                      <div className="aspect-square overflow-hidden bg-[#E9EBE4]">
                        <img
                          alt={trainer.name}
                          className="h-full w-full object-cover"
                          data-testid={`trainer-directory-image-${trainer.slug}`}
                          src={trainer.image}
                        />
                      </div>
                      <div className="space-y-3 p-5">
                        <p className="eyebrow" data-testid={`trainer-directory-category-${trainer.slug}`}>
                          {trainer.category}
                        </p>
                        <h3
                          className="text-2xl font-semibold tracking-tight text-[#1A2525]"
                          data-testid={`trainer-directory-name-${trainer.slug}`}
                        >
                          {trainer.name}
                        </h3>
                        <p
                          className="section-copy text-sm text-[#1A2525]"
                          data-testid={`trainer-directory-specialty-${trainer.slug}`}
                        >
                          {trainer.specialty}
                        </p>
                        <p
                          className="overflow-hidden text-ellipsis whitespace-nowrap text-sm text-[#4A5D5D]"
                          data-testid={`trainer-directory-summary-${trainer.slug}`}
                        >
                          {trainer.bio}
                        </p>
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#2D5A5A]" data-testid="umair-ansari-view-transformations">
                          View transformations
                          <InstagramLogo className="h-4 w-4" />
                        </span>
                      </div>
                    </Link>
                  )}

                  {trainer.name !== "Umair Ansari" && (
                    <>
                      <div className="aspect-square overflow-hidden bg-[#E9EBE4]">
                        <img
                          alt={trainer.name}
                          className="h-full w-full object-cover"
                          data-testid={`trainer-directory-image-${trainer.slug}`}
                          src={trainer.image}
                        />
                      </div>
                      <div className="space-y-3 p-5">
                        <p className="eyebrow" data-testid={`trainer-directory-category-${trainer.slug}`}>
                          {trainer.category}
                        </p>
                        <h3
                          className="text-2xl font-semibold tracking-tight text-[#1A2525]"
                          data-testid={`trainer-directory-name-${trainer.slug}`}
                        >
                          {trainer.name}
                        </h3>
                        <p
                          className="section-copy text-sm text-[#1A2525]"
                          data-testid={`trainer-directory-specialty-${trainer.slug}`}
                        >
                          {trainer.specialty}
                        </p>
                        <p
                          className="overflow-hidden text-ellipsis whitespace-nowrap text-sm text-[#4A5D5D]"
                          data-testid={`trainer-directory-summary-${trainer.slug}`}
                        >
                          {trainer.bio}
                        </p>
                        {trainer.instagram ? (
                          <a
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#2D5A5A] transition-colors duration-300 hover:text-[#1F4242]"
                            data-testid={`trainer-directory-instagram-${trainer.slug}`}
                            href={trainer.instagram}
                            rel="noreferrer"
                            target="_blank"
                          >
                            Instagram profile
                            <InstagramLogo className="h-4 w-4" />
                          </a>
                        ) : (
                          <div
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#758A8A]"
                            data-testid={`trainer-directory-instagram-pending-${trainer.slug}`}
                          >
                            Instagram coming soon
                            <InstagramLogo className="h-4 w-4" />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </RevealCard>
              ))
            ) : (
              <RevealCard className="floating-panel col-span-full p-8 text-center md:p-12">
                <p className="eyebrow" data-testid="coach-empty-state-eyebrow">Coaches roster cleared</p>
                <h3 className="mt-3 text-3xl font-semibold tracking-tight text-[#1A2525]" data-testid="coach-empty-state-title">
                  The coaches page is ready for your full team upload.
                </h3>
                <p className="mx-auto mt-4 max-w-2xl section-copy" data-testid="coach-empty-state-copy">
                  I’ve removed all current trainer profiles so you can add the entire Pulse Fitness coaching team in one clean batch.
                </p>
              </RevealCard>
            )}
          </div>
        </div>
      </section>

      <MobileBottomBar />
      <FooterSection />
    </main>
  );
};